import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import { PDFDocument } from 'pdf-lib';
import { SalonInvoiceProfile } from '../../services/peInvoiceFirebaseService';
import {
  InvoiceCustomer,
  InvoiceItem,
  InvoiceShipping,
  InvoiceMeta,
  InvoiceCalculations,
} from '../../store/usePEInvoiceStore';
import { generateEpcQrPayload } from './epcGiroCodeGenerator';
import {
  ESTIMATE_PROGNOSIS_DISCLAIMER,
  formatInvoiceItemServiceMeta,
} from './peInvoiceItemMeta';
import { getPeInvoiceSenderFromAppStore } from './peInvoiceSenderFromAppStore';
import { generateZugferdXml } from './zugferdGenerator';

export interface GeneratePEInvoicePdfParams {
  profile: SalonInvoiceProfile;
  customer: InvoiceCustomer;
  items: InvoiceItem[];
  shipping: InvoiceShipping;
  meta: InvoiceMeta;
  calculations: InvoiceCalculations;
}

function triggerPdfDownload(bytes: Uint8Array, filename: string): void {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function sanitizePdfFilenamePart(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]/g, '_');
}

function buildInvoicePdfFilename(meta: InvoiceMeta, customer: InvoiceCustomer): string {
  const docTypeLabel = meta.docType === 'estimate' ? 'Kostenvoranschlag' : 'Rechnung';
  const nr = sanitizePdfFilenamePart(meta.invoiceNumber || 'Entwurf');
  const rawName =
    customer.type === 'business'
      ? customer.companyName || 'Firma'
      : `${customer.firstName}_${customer.lastName}`.trim() || 'Kunde';
  const cleanName = sanitizePdfFilenamePart(rawName);
  return `${docTypeLabel}_${nr}_${cleanName}.pdf`;
}

/** Gemeinsamer jsPDF-Renderer für Standard-PDF und E-Rechnung Hybrid-PDF */
export async function createBasePdfDocument(
  params: GeneratePEInvoicePdfParams
): Promise<jsPDF> {
  const { profile, customer, items, shipping, meta, calculations } = params;
  const isEstimate = meta.docType === 'estimate';
  const sender = getPeInvoiceSenderFromAppStore();
  const senderName = sender.displayName || profile.accountHolder || 'Salon & Rechnungssteller';
  const kontoinhaber = profile.accountHolder || sender.accountHolder || senderName;

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let currentY = 25;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text(senderName, pageWidth - margin, currentY, { align: 'right' });
  currentY += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(71, 85, 105);
  if (sender.street) {
    doc.text(sender.street, pageWidth - margin, currentY, { align: 'right' });
    currentY += 4;
  }
  if (sender.zip || sender.city) {
    doc.text(`${sender.zip} ${sender.city}`.trim(), pageWidth - margin, currentY, {
      align: 'right',
    });
    currentY += 4;
  }
  if (profile.taxNumber) {
    doc.text(`Steuer-Nr.: ${profile.taxNumber}`, pageWidth - margin, currentY, {
      align: 'right',
    });
    currentY += 4;
  }
  if (profile.vatId) {
    doc.text(`USt-IdNr.: ${profile.vatId}`, pageWidth - margin, currentY, { align: 'right' });
    currentY += 4;
  }
  if (sender.email) {
    doc.text(sender.email, pageWidth - margin, currentY, { align: 'right' });
    currentY += 4;
  }
  currentY += 8;

  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  const returnLine = [senderName, sender.zip, sender.city].filter(Boolean).join(' · ');
  doc.text(returnLine, margin, currentY);
  currentY += 5;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);

  if (customer.type === 'business') {
    doc.text(customer.companyName || 'Firma', margin, currentY);
    currentY += 4.5;
    if (customer.contactPerson) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`z. Hd. ${customer.contactPerson}`, margin, currentY);
      currentY += 4.5;
    }
  } else {
    const fullName = `${customer.firstName} ${customer.lastName}`.trim() || 'Kunde';
    doc.text(fullName, margin, currentY);
    currentY += 4.5;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(51, 65, 85);
  if (customer.street) {
    doc.text(customer.street, margin, currentY);
    currentY += 4;
  }
  if (customer.zip || customer.city) {
    doc.text(`${customer.zip} ${customer.city}`.trim(), margin, currentY);
    currentY += 4;
  }
  if (customer.type === 'business' && customer.vatId) {
    doc.text(`USt-IdNr.: ${customer.vatId}`, margin, currentY);
    currentY += 4;
  }
  currentY += 12;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(15, 23, 42);
  doc.text(isEstimate ? 'KOSTENVORANSCHLAG' : 'RECHNUNG', margin, currentY);
  currentY += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(51, 65, 85);
  doc.text(
    `${isEstimate ? 'KVA-Nr.' : 'Rechnungs-Nr.'}: ${meta.invoiceNumber}`,
    margin,
    currentY
  );
  doc.text(`Datum: ${meta.invoiceDate}`, margin + 60, currentY);
  doc.text(
    `${isEstimate ? 'Gültig bis' : 'Leistungsdatum'}: ${meta.deliveryDate}`,
    margin + 115,
    currentY
  );
  if (customer.type === 'business' && customer.buyerReference) {
    currentY += 4.5;
    doc.text(`Leitweg-ID / Ref: ${customer.buyerReference}`, margin, currentY);
  }
  currentY += 8;

  doc.setDrawColor(30, 41, 59);
  doc.setLineWidth(0.7);
  doc.line(margin, currentY, pageWidth - margin, currentY);
  currentY += 6;

  // Noble Slate Tabellenkopf
  doc.setFillColor(30, 41, 59);
  doc.rect(margin, currentY, contentWidth, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(248, 250, 252);
  doc.text('Pos.', margin + 2, currentY + 4.8);
  doc.text('Beschreibung', margin + 12, currentY + 4.8);
  doc.text('Menge', margin + 95, currentY + 4.8, { align: 'right' });
  doc.text('Einzelpreis', margin + 125, currentY + 4.8, { align: 'right' });
  doc.text('MwSt', margin + 143, currentY + 4.8, { align: 'right' });
  doc.text('Gesamt', pageWidth - margin - 2, currentY + 4.8, { align: 'right' });
  currentY += 9;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(51, 65, 85);

  items.forEach((item, index) => {
    const lineTotal = (item.quantity || 0) * (item.unitPrice || 0);
    const vatLabel = calculations.isVatExempt ? '0%' : `${calculations.standardVatRate}%`;
    const priceModeLabel = item.priceMode === 'net' ? 'netto' : 'brutto';
    const serviceMeta = formatInvoiceItemServiceMeta(item);
    const descLines = doc.splitTextToSize(
      [item.description || 'Position', serviceMeta || (item.kind === 'sale' ? 'Verkauf' : '')]
        .filter(Boolean)
        .join('\n'),
      78
    );

    doc.text(`${index + 1}`, margin + 2, currentY);
    doc.text(descLines, margin + 12, currentY);
    doc.text(`${item.quantity} ${item.unit || 'Stk.'}`, margin + 95, currentY, { align: 'right' });
    doc.text(`${item.unitPrice.toFixed(2)} € (${priceModeLabel})`, margin + 125, currentY, {
      align: 'right',
    });
    doc.text(vatLabel, margin + 143, currentY, { align: 'right' });
    doc.text(`${lineTotal.toFixed(2)} €`, pageWidth - margin - 2, currentY, { align: 'right' });
    currentY += Math.max(5.5, descLines.length * 4 + 2);
  });

  if (shipping.enabled && shipping.amount > 0) {
    const shipVatLabel = calculations.isVatExempt ? '0%' : `${calculations.standardVatRate}%`;
    doc.text(`${items.length + 1}`, margin + 2, currentY);
    doc.text('Versand- & Verpackungskosten', margin + 12, currentY);
    doc.text('1 Pauschal', margin + 95, currentY, { align: 'right' });
    doc.text(`${shipping.amount.toFixed(2)} €`, margin + 125, currentY, { align: 'right' });
    doc.text(shipVatLabel, margin + 143, currentY, { align: 'right' });
    doc.text(`${shipping.amount.toFixed(2)} €`, pageWidth - margin - 2, currentY, { align: 'right' });
    currentY += 5.5;
  }

  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.4);
  doc.line(margin, currentY, pageWidth - margin, currentY);
  currentY += 5;

  const totalsX = pageWidth - margin - 55;
  const valuesX = pageWidth - margin - 2;
  const totalsBoxW = 58;
  const totalsBoxX = pageWidth - margin - totalsBoxW;

  // Summenblock: Netto (inkl. Versand-Pos.) → USt → Brutto — kein Versand darunter
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.3);
  const totalsStartY = currentY - 1;
  let totalsH = 14;
  if (!calculations.isVatExempt && calculations.vatBreakdown.standard.vat > 0) totalsH += 4.5;
  if (calculations.isVatExempt) totalsH += 4.5;
  doc.rect(totalsBoxX - 2, totalsStartY, totalsBoxW + 2, totalsH + 4, 'FD');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(71, 85, 105);
  doc.text('Zwischensumme Netto:', totalsX, currentY);
  doc.setTextColor(15, 23, 42);
  doc.text(`${calculations.netTotal.toFixed(2)} €`, valuesX, currentY, { align: 'right' });
  currentY += 4.5;

  if (calculations.isVatExempt) {
    doc.setFontSize(8.5);
    doc.setTextColor(100, 116, 139);
    doc.text('Umsatzsteuerbefreit – keine USt.', totalsX, currentY);
    doc.setTextColor(51, 65, 85);
    doc.setFontSize(9.5);
    currentY += 4.5;
  } else if (calculations.vatBreakdown.standard.vat > 0) {
    doc.setTextColor(71, 85, 105);
    doc.text(`zzgl. USt ${calculations.standardVatRate}%:`, totalsX, currentY);
    doc.setTextColor(15, 23, 42);
    doc.text(`${calculations.vatBreakdown.standard.vat.toFixed(2)} €`, valuesX, currentY, {
      align: 'right',
    });
    currentY += 4.5;
  }

  doc.setDrawColor(30, 41, 59);
  doc.setLineWidth(0.35);
  doc.line(totalsX, currentY - 1.5, valuesX, currentY - 1.5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(15, 23, 42);
  doc.text('Gesamtbetrag (Brutto):', totalsX, currentY + 1);
  doc.text(`${calculations.grossTotal.toFixed(2)} €`, valuesX, currentY + 1, { align: 'right' });
  currentY += 14;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(71, 85, 105);
  if (isEstimate) {
    const validity = `Dieses Angebot ist freibleibend und gültig für ${meta.paymentDeadlineDays} Tage bis zum ${meta.deliveryDate}.`;
    const validityLines = doc.splitTextToSize(validity, contentWidth);
    doc.text(validityLines, margin, currentY);
    currentY += validityLines.length * 4 + 2;
    const disclaimerLines = doc.splitTextToSize(ESTIMATE_PROGNOSIS_DISCLAIMER, contentWidth);
    doc.setFontSize(9);
    doc.text(disclaimerLines, margin, currentY);
    doc.setFontSize(9.5);
    currentY += disclaimerLines.length * 3.5 + 2;
  } else {
    doc.text(
      `Zahlbar innerhalb von ${meta.paymentDeadlineDays} Tagen ohne Abzug.`,
      margin,
      currentY
    );
    currentY += 4.5;
  }

  if (meta.notes) {
    doc.text(meta.notes, margin, currentY);
    currentY += 8;
  } else {
    currentY += 4;
  }

  if (!isEstimate) {
    const qrPayload = generateEpcQrPayload({
      bic: profile.bic,
      accountHolder: kontoinhaber,
      iban: profile.iban,
      amount: calculations.grossTotal,
      invoiceNumber: meta.invoiceNumber,
    });

    if (qrPayload) {
      try {
        const canvas = document.createElement('canvas');
        await QRCode.toCanvas(canvas, qrPayload, { width: 100, margin: 1 });
        const qrDataUrl = canvas.toDataURL('image/png');

        doc.setDrawColor(203, 213, 225);
        doc.setFillColor(248, 250, 252);
        doc.setLineWidth(0.35);
        doc.roundedRect(margin, currentY - 2, contentWidth, 32, 2, 2, 'FD');

        doc.addImage(qrDataUrl, 'PNG', margin + 3, currentY + 1, 26, 26);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.setTextColor(15, 23, 42);
        doc.text('Bequem per Banking-App scannen & bezahlen', margin + 34, currentY + 5);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(71, 85, 105);
        doc.text(`Empfänger: ${kontoinhaber}`, margin + 34, currentY + 10);
        doc.text(`IBAN: ${profile.iban}`, margin + 34, currentY + 14.5);
        if (profile.bic) doc.text(`BIC: ${profile.bic}`, margin + 34, currentY + 19);
        doc.text(`Bank: ${profile.bankName}`, margin + 34, currentY + 23.5);
        currentY += 34;
      } catch {
        doc.text(
          `IBAN: ${profile.iban} | BIC: ${profile.bic} | Bank:${profile.bankName}`,
          margin,
          currentY
        );
      }
    } else if (profile.iban) {
      doc.text(
        `IBAN: ${profile.iban} | BIC: ${profile.bic} | Bank:${profile.bankName}`,
        margin,
        currentY
      );
    }
  }

  doc.setFontSize(8.5);
  doc.setTextColor(148, 163, 184);
  const footerText = `${senderName} • ${profile.bankName || ''} • Steuernummer: ${profile.taxNumber || 'k.A.'}`;
  doc.text(footerText, pageWidth / 2, pageHeight - 12, { align: 'center' });

  return doc;
}

export async function generatePEInvoicePdf(params: GeneratePEInvoicePdfParams): Promise<void> {
  const doc = await createBasePdfDocument(params);
  doc.save(buildInvoicePdfFilename(params.meta, params.customer));
}

/** ZUGFeRD 2.1 / Factur-X: sichtbares PDF + eingebettete factur-x.xml */
export async function generatePEInvoiceHybridPdf(
  params: GeneratePEInvoicePdfParams
): Promise<void> {
  const baseDoc = await createBasePdfDocument(params);
  const pdfBytes = baseDoc.output('arraybuffer');

  const xmlContent = generateZugferdXml(params);
  const xmlBytes = new TextEncoder().encode(xmlContent);

  const pdfDoc = await PDFDocument.load(pdfBytes);
  await pdfDoc.attach(xmlBytes, 'factur-x.xml', {
    mimeType: 'text/xml',
    description: 'Factur-X / ZUGFeRD 2.1 Invoice XML',
    creationDate: new Date(),
    modificationDate: new Date(),
  });

  const hybridPdfBytes = await pdfDoc.save();
  triggerPdfDownload(
    hybridPdfBytes,
    buildInvoicePdfFilename(params.meta, params.customer)
  );
}
